import axios from 'axios';
import Swal from 'sweetalert2'
import { useState } from 'react';
import { TechTool } from 'types/TechTool';
import { useAuth } from 'context/useAuth';
import { useNavigate } from 'react-router-dom';
import withReactContent from 'sweetalert2-react-content';
import { useAppSettings } from 'context/appsettingsContext';
import TechToolList from 'components/TechToolList/TechToolList';
import TechToolCard from 'components/TechToolCard/TechToolCard';
import { errorAsync, successAsync } from 'functions/notification';
import FilterNavigation from 'components/FilterNavigation/FilterNavigation';
import { FirestoreCollection, useCollection } from 'functions/hooks/useCollection';
import { getDisplayDateFromTimestamp, isValidDeviceId, isValidRemoteId, requestDeviceStatus, updateAutoLock } from 'functions/deviceStatusUtils';
import { where } from 'firebase/firestore';

const tools: TechTool[] = [];
const swal = withReactContent(Swal);

export const SystemState: Record<number, string> = {
    0: "Unlocked",
    1: "Normal Locked",
    2: "Admin Locked",
    3: "Hijack Locked",
    4: "Override",
    255: "Unknown"
};

tools.push({
    title: "Add Remote",
    launch: async ({ submit }) => {
        await swal.fire({
            title: 'Add Remote',
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            html: <>
                <div>
                    <input id="deviceId" className="swal2-input" placeholder="Device ID" />
                </div>
                <div>
                    <input id="remoteId" className="swal2-input" placeholder="Remote ID" />
                </div>
                <div className='swal2-checkbox'>
                    <input id="isPanic" name="isPanic" type="checkbox" />
                    <label htmlFor="isPanic">Is Panic Button</label>
                </div>
            </>,
            preConfirm: async () => {

                const popup = swal.getPopup()!;
                const get = (id: string) => popup.querySelector(`#${id}`) as any;
                let deviceId: string = get('deviceId').value.trim();
                let remoteId: string = get('remoteId').value.trim();
                let isPanic: boolean = get('isPanic').checked;

                if (!deviceId || !isValidDeviceId(deviceId)) {
                    swal.showValidationMessage('Valid Device ID is required');
                    return false;
                }

                if (!remoteId || !isValidRemoteId(remoteId)) {
                    swal.showValidationMessage('Valid Remote ID is required');
                    return false;
                }

                await submit('addRemote', {
                    deviceId: deviceId,
                    remoteId: remoteId,
                    isPanic: isPanic,
                });
            }
        })
    }
});

tools.push({
    title: "Remove Remote",
    launch: async ({ submit }) => {
        await swal.fire({
            title: 'Remove Remote',
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            html: <>
                <div>
                    <input id="deviceId" className="swal2-input" placeholder="Device ID" />
                </div>
                <div>
                    <input id="remoteId" className="swal2-input" placeholder="Remote ID" />
                </div>
            </>,
            preConfirm: async () => {

                const popup = swal.getPopup()!;
                const get = (id: string) => popup.querySelector(`#${id}`) as any;
                let deviceId: string = get('deviceId').value.trim();
                let remoteId: string = get('remoteId').value.trim();

                if (!deviceId || !isValidDeviceId(deviceId)) {
                    swal.showValidationMessage('Valid Device ID is required');
                    return false;
                }

                if (!remoteId || !isValidRemoteId(remoteId)) {
                    swal.showValidationMessage('Valid Remote ID is required');
                    return false;
                }

                await submit('removeRemote', {
                    deviceId: deviceId,
                    remoteId: remoteId,
                });
            }
        })
    }
});

tools.push({
    title: "Replace Remote",
    launch: async ({ submit }) => {
        await swal.fire({
            title: 'Replace Remote',
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            html: <>
                <div>
                    <input id="deviceId" className="swal2-input" placeholder="Device ID" />
                </div>
                <div>
                    <input id="removeId" className="swal2-input" placeholder="Remove Remote ID" />
                </div>
                <div>
                    <input id="addId" className="swal2-input" placeholder="Add Remote ID" />
                </div>
                <div className='swal2-checkbox'>
                    <input id="isPanic" name="isPanic" type="checkbox" />
                    <label htmlFor="isPanic">Is Panic Button</label>
                </div>
            </>,
            preConfirm: async () => {

                const popup = swal.getPopup()!;
                const get = (id: string) => popup.querySelector(`#${id}`) as any;
                let deviceId: string = get('deviceId').value.trim();
                let addId: string = get('addId').value.trim();
                let removeId: string = get('removeId').value.trim();
                let isPanic: boolean = get('isPanic').checked;

                if (!deviceId || !isValidDeviceId(deviceId)) {
                    swal.showValidationMessage('Valid Device ID is required');
                    return false;
                }

                if (!removeId || !isValidRemoteId(removeId)) {
                    swal.showValidationMessage('Valid Remove ID is required');
                    return false;
                }

                if (!addId || !isValidRemoteId(addId)) {
                    swal.showValidationMessage('Valid Add ID is required');
                    return false;
                }

                await submit('replaceRemote', {
                    deviceId: deviceId,
                    removeId: removeId,
                    addId: addId,
                    isPanic: isPanic
                });
            }
        })
    }
});

tools.push({
    title: "Replace Device",
    launch: async ({ navigate, vehiclesCollection }) => {

        const typedVehiclesCollection = vehiclesCollection as FirestoreCollection<any>;

        await swal.fire({
            title: 'Replace Device',
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            html: <>
                <div>
                    <input id="oldDeviceId" className="swal2-input" placeholder="Old Device ID" />
                </div>
                <div>
                    <input id="newDeviceId" className="swal2-input" placeholder="New Device ID" />
                </div>
            </>,
            preConfirm: async () => {

                const popup = swal.getPopup()!;
                const get = (id: string) => popup.querySelector(`#${id}`) as any;

                const oldDeviceId: string = get('oldDeviceId').value.trim();
                const newDeviceId: string = get('newDeviceId').value.trim();

                if (!oldDeviceId || !isValidDeviceId(oldDeviceId)) {
                    swal.showValidationMessage('Valid Old Device ID is required');
                    return false;
                }

                if (!newDeviceId || !isValidDeviceId(newDeviceId)) {
                    swal.showValidationMessage('Valid New Device ID is required');
                    return false;
                }

                const vehicle = (await typedVehiclesCollection.queryOne([where('deviceId', '==', newDeviceId)]))?.data as any;

                if (vehicle) {
                    swal.showValidationMessage(`Device ID '${newDeviceId}' is already assigned to another vehicle '${vehicle.details?.registrationNumber}'`);
                    return false;
                }

                await navigate(`/agent/installations/@new?replace=${oldDeviceId}|with|${newDeviceId}`);
            }
        });
    }
});

tools.push({
    title: "Check Device Status",
    launch: async ({ settings, authorisedUser, devicesCollection }) => {

        let statusRequested = false;
        let deviceBefore: any = null;
        let stopStream: (() => void) | undefined;
        const typedDevicesCollection = devicesCollection as FirestoreCollection<any>;

        await swal.fire({
            title: "Check Device Status",
            showCancelButton: true,
            confirmButtonText: "Check",
            cancelButtonText: "Close",
            allowOutsideClick: false,
            allowEscapeKey: false,
            html: (
                <>
                    <input id="deviceId" className="swal2-input" placeholder="Device ID" />
                    <div id="status" style={{ minHeight: '100px', color: 'black' }}></div>
                </>
            ),
            willClose: () => {
                if (stopStream) {
                    try { stopStream() } catch { /* Ignore */ }
                }
            },
            preConfirm: async () => {
                
                // init

                const popup = swal.getPopup()!;
                const status = popup.querySelector("#status") as HTMLDivElement;
                const deviceId = (popup.querySelector("#deviceId") as any).value.trim();

                if (!deviceId || !isValidDeviceId(deviceId)) {
                    swal.showValidationMessage("Valid Device ID is required");
                    return false;
                }

                // start

                if (stopStream) {
                    try { stopStream() } catch { /* Ignore */ }
                }
                
                statusRequested = false;
                status.innerHTML = "<br/>Waiting for response ...";
                deviceBefore = await typedDevicesCollection.getOne(deviceId);

                stopStream = typedDevicesCollection.getOneStream(deviceId, deviceAfter => {
                    if (statusRequested && deviceAfter?.StatusUpdateLog?.logTimestamp > (deviceBefore?.StatusUpdateLog?.logTimestamp || 0)) {
                        let stateHtml = '';
                        stateHtml += `<br/>Ignition : ${(deviceAfter?.StatusUpdateLog?.ignitionOn ? 'On' : 'Off')}`;
                        stateHtml += `<br/>Lock : ${SystemState[deviceAfter?.StatusUpdateLog?.currentState] || ''}`;
                        stateHtml += `<br/>Last Location : <a style="background-color: transparent; border: none; text-decoration: none" href="https://maps.google.com?q=${deviceAfter?.LastLocation?.latLng?.latitudeDeg || ''},${deviceAfter?.LastLocation?.latLng?.longitudeDeg || ''}" target="_blank" rel="noopener noreferrer">${deviceAfter?.LastLocation?.latLng?.latitudeDeg || ''}, ${deviceAfter?.LastLocation?.latLng?.longitudeDeg || ''}</a>`;
                        stateHtml += `<br/>Last Location Time : ${ getDisplayDateFromTimestamp(deviceAfter?.LastLocation?.updatedAt) || ''}`;
                        status.innerHTML = stateHtml;
                    }
                });

                try {
                    await requestDeviceStatus(settings.statusRequestCommandUrl, authorisedUser.accessToken, deviceId);
                    statusRequested = true;
                } catch (error) {
                    status.innerHTML = `<br/>Failed to request status`;
                }

                return false;
            }
        });
    }
});

tools.push({
    title: "Update Auto-Lock",
    launch: async ({ submit, settings, authorisedUser }) => {
        await swal.fire({
            title: 'Update Auto-Lock',
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            html: <>
                <div>
                    <input id="deviceId" className="swal2-input" placeholder="Device ID" />
                </div>
                <div>
                    <select id="delaySeconds" className="swal2-select" title='Delay Seconds'>
                        <option value="">Delay Seconds</option>
                        <option value="20">20 Seconds</option>
                        <option value="60">60 Seconds</option>
                    </select>
                </div>
            </>,
            preConfirm: async () => {

                const popup = swal.getPopup()!;
                const get = (id: string) => popup.querySelector(`#${id}`) as any;
                let deviceId: string = get('deviceId').value.trim();
                let delaySeconds: string = get('delaySeconds').value.trim();

                if (!deviceId || !isValidDeviceId(deviceId)) {
                    swal.showValidationMessage('Valid Device ID is required');
                    return false;
                }

                if (!delaySeconds || isNaN(Number(delaySeconds))) {
                    swal.showValidationMessage('Valid Delay Seconds is required');
                    return false;
                }

                try {
            
                    await updateAutoLock(settings.updateDeviceAutoLockUrl, authorisedUser.accessToken, deviceId, Number(delaySeconds));

                    successAsync("Auto-Lock Updated");

                } catch(error: any) {
                    errorAsync(error?.response?.data?.message || error?.message || error, error);
                }
            }
        })
    }
});

export default function TechTools() {

    const navigate = useNavigate();
    const settings = useAppSettings();
    const { authorisedUser } = useAuth();
    const [searchText, setSearchText] = useState('');
    const devicesCollection = useCollection<any>('devices');
    const vehiclesCollection = useCollection<any>('vehicles');
    const [allTools] = useState(tools.sort((a, b) => a.title.localeCompare(b.title)));
    
    async function submit(tool: string, data: any) {
        try {
            
            const response = await axios.post(
                settings.executeToolUrl,
                JSON.stringify({ tool: tool, data: data }),
                { headers: { Authorization: `Bearer ${authorisedUser.accessToken}`, 'Content-Type': 'application/json' } }
            );

            successAsync(response.data?.message);

        } catch(error: any) {
            errorAsync(error?.response?.data?.message || error?.message || error, error);
        }
    }

    return (
        <>
            <FilterNavigation filters={[]} hideSort={true} sortOptions={[]} onSearchChange={searchText => setSearchText(searchText?.toLocaleLowerCase()?.trim())} />
            <div className="all-tools">
                <TechToolList>
                    {allTools.filter((tool) => !searchText || `${tool.title}`.toLocaleLowerCase()?.indexOf(searchText) > -1).map((tool) => (
                        <TechToolCard key={tool.title} techtool={tool} onClick={() => tool.launch({
                            submit,
                            navigate,
                            settings,
                            authorisedUser,
                            devicesCollection,
                            vehiclesCollection,
                         })} />
                    ))}
                </TechToolList>
            </div>
        </>
    );
}